import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload, CheckCircle, RotateCcw, Trash2 } from 'lucide-react';

import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import {
  Container,
  StickyHeader,
  Title,
  Card,
  DropZone,
  DropText,
  FileName,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  Footer,
  HistoryTable,
  SectionTitle,
} from '../../components/import/ImportStyles';
import {
  getOpeningBalanceTemplate,
  validateOpeningBalance,
  executeOpeningBalance,
  getOpeningBalances,
  deleteOpeningBalance,
} from '../../services/leaveOpeningBalanceService';

// Devir (açılış) izin bakiyesi ekranı. Akış LeaveImportPage ile birebir aynı:
// şablon indir → dosya seç (otomatik doğrulanır) → önizleme → kaydet.
// Tür seçimi YOKTUR: tür her satırda dosyanın kendisinde yazar.
//
// KRİTİK: execute'un DÖNDÜĞÜ satırlar gösterilir, gönderilenler değil —
// önizlemede yeşil görünen bir satır sunucuda reddedilmiş olabilir.
function translateRowError(t, key) {
  return t(`responseErrors.${key}`, { defaultValue: t(key, { defaultValue: key }) });
}

export default function LeaveOpeningBalancePage() {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [existing, setExisting] = useState([]);
  const fileInputRef = useRef(null);

  const loadExisting = useCallback(async () => {
    const res = await getOpeningBalances();
    // api.js GET okumalarında reject ETMEZ — gerçek başarı sinyali res.data.status.
    if (!res?.data?.status) return;
    setExisting(res.data.data ?? []);
  }, []);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const validRows = result?.validRows ?? 0;
  const newRows = result?.newRows ?? 0;
  const updateRows = result?.updateRows ?? 0;
  const errorRows = result?.errorRows ?? 0;
  const rows = useMemo(() => result?.rows ?? [], [result]);

  const handleDownloadTemplate = useCallback(async () => {
    const res = await getOpeningBalanceTemplate();
    // 403/hata durumunda res.data gerçek bir Blob DEĞİL, EMPTY_RESPONSE gövdesidir.
    // Denetlemeden indirilirse tarayıcı birkaç baytlık bozuk bir .xlsx kaydeder.
    if (!(res?.data instanceof Blob) || res.data.size < 100) return;
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'flexbee-devir-bakiyesi-sablonu.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const handleFileChosen = useCallback(async (file) => {
    if (!file || !file.name.endsWith('.xlsx')) return;
    setFileName(file.name);
    setSaved(null);
    setBusy(true);
    try {
      const res = await validateOpeningBalance(file);
      if (res?.data?.status) setResult(res.data.data);
    } catch {
      // api.js interceptor toast'ladı; finally busy'yi geri açsın diye yakalıyoruz.
    } finally {
      setBusy(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    handleFileChosen(e.target.files?.[0]);
    e.target.value = ''; // aynı dosya tekrar seçilebilsin
  }, [handleFileChosen]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    handleFileChosen(e.dataTransfer.files?.[0]);
  }, [handleFileChosen, busy]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    try {
      const res = await executeOpeningBalance(rows);
      if (res?.data?.status) {
        setResult(res.data.data);
        setSaved(res.data.data.validRows ?? 0);
        await loadExisting();
      }
    } catch {
      // Handled by interceptor
    } finally {
      setBusy(false);
    }
  }, [rows, loadExisting]);

  const handleDelete = useCallback(async (id) => {
    setBusy(true);
    try {
      const res = await deleteOpeningBalance(id);
      if (res?.data?.status) await loadExisting();
    } catch {
      // Handled by interceptor
    } finally {
      setBusy(false);
    }
  }, [loadExisting]);

  const handleReset = useCallback(() => {
    setResult(null);
    setFileName('');
    setSaved(null);
  }, []);

  return (
    <Container>
      <StickyHeader>
        <Title>{t('leaveOpeningBalance.title')}</Title>
      </StickyHeader>

      <Card>
        <SectionTitle>{t('leaveOpeningBalance.howItWorks')}</SectionTitle>
        <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', lineHeight: 1.6 }}>
          {t('leaveOpeningBalance.description')}
        </p>
        <Footer>
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download size={16} style={{ marginRight: 8 }} />
            {t('leaveOpeningBalance.downloadTemplate')}
          </Button>
          <div />
        </Footer>
      </Card>

      <Card>
        <SectionTitle>{t('leaveOpeningBalance.uploadFile')}</SectionTitle>

        <DropZone
          $dragOver={dragOver}
          onClick={() => { if (!busy) fileInputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={busy ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <Upload size={32} color="#667085" />
          <DropText>
            {fileName ? <FileName>{fileName}</FileName> : t('leaveOpeningBalance.dropzone')}
          </DropText>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            data-testid="opening-balance-file"
            disabled={busy}
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
        </DropZone>

        {result && (
          <>
            <SummaryGrid style={{ marginTop: 20 }}>
              <SummaryCard $color="#F0FDF4">
                <SummaryValue>{newRows}</SummaryValue>
                <SummaryLabel>{t('leaveOpeningBalance.newRows')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#EFF6FF">
                <SummaryValue>{updateRows}</SummaryValue>
                <SummaryLabel>{t('leaveOpeningBalance.updateRows')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#FEF2F2">
                <SummaryValue>{errorRows}</SummaryValue>
                <SummaryLabel>{t('leaveOpeningBalance.hasError')}</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            <HistoryTable>
              <thead>
                <tr>
                  <th>{t('leaveOpeningBalance.rowNumber')}</th>
                  <th>{t('employee.registrationNumber')}</th>
                  <th>{t('employee.fullName')}</th>
                  <th>{t('leaveOpeningBalance.variation')}</th>
                  <th>{t('leaveOpeningBalance.days')}</th>
                  <th>{t('leaveOpeningBalance.effectiveDate')}</th>
                  <th>{t('leaveOpeningBalance.error.title')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.rowNumber} style={!r.isValid ? { background: '#FEF2F2' } : undefined}>
                    <td>{r.rowNumber}</td>
                    <td>{r.registrationNumber}</td>
                    <td>{r.fullName}</td>
                    <td>{r.leaveVariationName}</td>
                    <td>{r.days ?? '-'}</td>
                    <td>{r.effectiveDate ? new Date(r.effectiveDate).toLocaleDateString() : '-'}</td>
                    <td>{r.errorKey ? translateRowError(t, r.errorKey) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          </>
        )}

        <Footer>
          <div>
            {saved !== null && (
              <Tooltip text={t('leaveOpeningBalance.resetTooltip')}>
                <IconButton onClick={handleReset}>
                  <RotateCcw size={16} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          {saved === null && (
            <Button
              data-testid="opening-balance-submit"
              disabled={validRows === 0 || busy}
              onClick={handleSave}
            >
              <CheckCircle size={16} style={{ marginRight: 8 }} />
              {`${t('leaveOpeningBalance.save')} (${validRows})`}
            </Button>
          )}
        </Footer>

        {saved !== null && (
          <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', marginTop: 8 }}>
            <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {t('leaveOpeningBalance.savedSummary', { count: saved })}
          </p>
        )}
      </Card>

      {existing.length > 0 && (
        <Card>
          <SectionTitle>{t('leaveOpeningBalance.currentTitle')}</SectionTitle>
          <HistoryTable>
            <thead>
              <tr>
                <th>{t('employee.registrationNumber')}</th>
                <th>{t('employee.fullName')}</th>
                <th>{t('leaveOpeningBalance.variation')}</th>
                <th>{t('leaveOpeningBalance.days')}</th>
                <th>{t('leaveOpeningBalance.effectiveDate')}</th>
                <th>{t('leaveOpeningBalance.note')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {existing.map((b) => (
                <tr key={b.id}>
                  <td>{b.registrationNumber}</td>
                  <td>{b.fullName}</td>
                  <td>{b.leaveVariationName}</td>
                  <td>{b.days}</td>
                  <td>{b.effectiveDate ? new Date(b.effectiveDate).toLocaleDateString() : '-'}</td>
                  <td>{b.note}</td>
                  <td>
                    <Tooltip text={t('common.delete')}>
                      <IconButton disabled={busy} onClick={() => handleDelete(b.id)}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </HistoryTable>
        </Card>
      )}
    </Container>
  );
}
